import type { SentryEvent } from './types';
import { redactString, redactValue } from './redact';
import { scrubRequest } from './scrub-request';
import { scrubUrl } from './scrub-url';
import { truncate } from './truncate';

const redactFreeText = (value: string) => truncate(redactString(value));

/**
 * The single `beforeSend` / `beforeSendTransaction` hook for every Sentry SDK in the
 * monorepo. Mutates the event in place and returns the same reference, so it is a
 * drop-in for both `@sentry/node` (v7) and `@sentry/react` (v10) without casts at the
 * call site: `Sentry.init({ beforeSend: scrubSentryEvent })`.
 *
 * The generic is `<T extends object>` rather than the structural `SentryEvent` so the
 * caller's own event type flows straight through — no assignability friction with the
 * SDK's `Event`, no import of any `@sentry/*` type.
 *
 * Pipeline (order matters): request → user → messages/exceptions → breadcrumbs →
 * extra/contexts. Stack-frame filenames/functions are deliberately left untouched so
 * traces stay readable.
 */
export function scrubSentryEvent<T extends object>(event: T): T {
  const sentryEvent = event as unknown as SentryEvent;

  if (sentryEvent.request) {
    scrubRequest(sentryEvent.request);
  }

  // Transaction name is URL-derived and bypasses request scrubbing. Route-template it
  // (so `/reports/<cuid>/summary` → `/reports/:id/summary`), then redact any embedded
  // value the segment templating can't catch (e.g. an email in a path segment).
  if (typeof sentryEvent.transaction === 'string') {
    sentryEvent.transaction = redactString(scrubUrl(sentryEvent.transaction));
  }

  // No user identity at all — not even an id (BAL-7806).
  sentryEvent.user = {};

  if (typeof sentryEvent.message === 'string') {
    sentryEvent.message = redactFreeText(sentryEvent.message);
  }

  if (sentryEvent.exception?.values) {
    for (const exception of sentryEvent.exception.values) {
      if (typeof exception.value === 'string') {
        exception.value = redactFreeText(exception.value);
      }
    }
  }

  if (sentryEvent.breadcrumbs) {
    for (const breadcrumb of sentryEvent.breadcrumbs) {
      if (typeof breadcrumb.message === 'string') {
        breadcrumb.message = redactFreeText(breadcrumb.message);
      }

      if (breadcrumb.data) {
        breadcrumb.data = redactValue(breadcrumb.data) as Record<string, unknown>;
      }
    }
  }

  // Fingerprints are caller-built grouping keys and have carried raw tokens/ids.
  if (sentryEvent.fingerprint) {
    sentryEvent.fingerprint = sentryEvent.fingerprint.map(part =>
      typeof part === 'string' ? redactString(part) : part,
    );
  }

  if (sentryEvent.extra) {
    sentryEvent.extra = redactValue(sentryEvent.extra) as Record<string, unknown>;
  }

  if (sentryEvent.contexts) {
    sentryEvent.contexts = redactValue(sentryEvent.contexts) as SentryEvent['contexts'];
  }

  return event;
}
